import {
  Component,
  Input,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { OffersOfApplication } from '../../../../type/result_type';
import { FocusManagementService } from '../../../../service/focus-management.service';
import { BreakpointService } from '../../../../service/breakpoint.service';
import { BankDetails } from 'src/openapi-ts'; 

@Component({
  selector: 'app-conditions',
  templateUrl: './conditions.component.html',
  styleUrls: ['./conditions.component.scss'],
})
export class ConditionsComponent implements OnInit, AfterViewInit {
  @Input() conditionsData!: OffersOfApplication;
  @Input() fullWidth: boolean = false;
  @Input() focusParentTab: () => void;
  @ViewChildren('conditionContent') conditionContents!: QueryList<ElementRef>;

  showInterestExplanation = false;
  private readonly preliminaryOfferStates = ['PRELIMINARY_OFFER'];
  public showInterestExplanationItems = [
    'PRELIMINARY_OFFER',
    'EXPIRED',
    'DOCUMENTS_UPLOADED',
    'DOCUMENTS_MISSING',
    'DOCUMENTS_CHECKED',
    'REJECTED_CLEARING',
    'REJECTED',
    'DOCUMENTS_MISSING_2ND',
    'DOCUMENTS_CHECKED_2ND',
    'DOCUMENTS_UPLOADED_2ND',
    'REJECTED_2ND',
    'NEW',
  ];

  constructor(
    private focusManagementService: FocusManagementService,
    private breakpointService: BreakpointService,
  ) {}

  get windowSize() {
    return this.breakpointService.windowSize;
  }

  ngOnInit(): void {
    this.showInterestExplanation = this.showInterestExplanationItems.includes(
      this.conditionsData?.contractState
    );
  }

  ngAfterViewInit(): void {
    this.focusManagementService.setupKeydownListeners(
      this.conditionContents,
      this.focusParentTab
    );
  }

  hasCreditProtectionSelected(): boolean {
    return (
      this.conditionsData?.offerResponse?.terms?.creditProtection1?.type !==
      'RSV_NONE'
    );
  }

  hasSeparateRsvBilling(): boolean {
    return (
      this.conditionsData?.offerResponse?.terms?.monthlyInstallmentWithoutRsv !=
      null
    );
  }

  isPreliminaryOffer(): boolean {
    return this.preliminaryOfferStates.includes(
      this.conditionsData?.contractState
    );
  }

  getMonthlyRateWithoutRsv(): number | null {
    const terms = this.conditionsData?.offerResponse?.terms;
    if (!terms?.monthlyInstallment) {
      return null;
    }

    if (this.hasSeparateRsvBilling()) {
      return terms.monthlyInstallmentWithoutRsv!;
    }

    const monthlyCreditProtection = this.getMonthlyCreditProtection();
    if (this.hasCreditProtectionSelected() && monthlyCreditProtection > 0) {
      return terms.monthlyInstallment - monthlyCreditProtection;
    }

    return terms.monthlyInstallment;
  }

  getMonthlyCreditProtection(): number {
    const terms = this.conditionsData?.offerResponse?.terms;
    const creditProtection1Fee = terms?.creditProtection1?.totalFee ?? 0;
    const creditProtection2Fee = terms?.creditProtection2?.totalFee ?? 0;
    const totalFee = creditProtection1Fee + creditProtection2Fee;

    if (totalFee > 0 && terms?.numberOfInstallments) {
      return totalFee / terms.numberOfInstallments;
    }

    if (
      terms?.monthlyInstallment != null &&
      terms?.monthlyInstallmentWithoutRsv != null
    ) {
      return terms.monthlyInstallment - terms.monthlyInstallmentWithoutRsv;
    }

    return 0;
  }

  getMonthlyTotalCosts(): number | null {
    const terms = this.conditionsData?.offerResponse?.terms;
    if (!terms?.monthlyInstallment) {
      return null;
    }

    const accountFeeTotal = terms.accountFeeTotal ?? 0;

    if (!this.hasCreditProtectionSelected() || !this.hasSeparateRsvBilling()) {
      return terms.monthlyInstallment + accountFeeTotal;
    }

    if (this.isPreliminaryOffer()) {
      return terms.monthlyInstallmentWithoutRsv! + accountFeeTotal;
    }

    return (
      terms.monthlyInstallmentWithoutRsv! +
      this.getMonthlyCreditProtection() +
      accountFeeTotal
    );
  }

  formatCurrency(value: number): string {
    const formatted = value.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return this.isPreliminaryOffer() ? `ab ${formatted}` : formatted;
  }

  getCreditProtectionPackageName(): string | null {
    switch (this.conditionsData?.offerResponse?.terms?.creditProtection1?.type) {
      case 'RSV_TOD':
        return 'Basis';
      case 'RSV_BU':
        return 'Standard';
      case 'RSV_ALO':
        return 'Komplett';
      default:
        return null;
    }
  }

  getCreditProtectionTotal(): number | null {
    if (!this.hasCreditProtectionSelected()) {
      return null;
    }

    const terms = this.conditionsData?.offerResponse?.terms;
    const totalFee =
      (terms?.creditProtection1?.totalFee ?? 0) +
      (terms?.creditProtection2?.totalFee ?? 0);
    if (totalFee > 0) {
      return totalFee;
    }

    const monthly = this.getMonthlyCreditProtection();
    if (monthly > 0 && terms?.numberOfInstallments) {
      return monthly * terms.numberOfInstallments;
    }

    return null;
  }

  getAmountDue(): number | null {
    const loanTotalAmount =
      this.conditionsData?.offerResponse?.terms?.loanTotalAmount;
    if (!loanTotalAmount) {
      return null;
    }

    return loanTotalAmount + (this.getCreditProtectionTotal() ?? 0);
  }

  getInterestAndFees(): number | null {
    const terms = this.conditionsData?.offerResponse?.terms;
    if (!terms?.loanTotalAmount || terms.loanAmount == null) {
      return null;
    }

    const interestAndFees = terms.loanTotalAmount - terms.loanAmount;
    return interestAndFees < 0 ? null : interestAndFees;
  }

  isSantander(): boolean {
    return this.conditionsData?.bankProduct?.bankDetails?.bankGateway === BankDetails.BankGatewayEnum.Santander;
  }

  getEffectiveInterestNominalInterestTitleForSantander() : string {
    if (this.windowSize === 'mobile' && this.isSantander()) {
      return "Vrstl. eff. Jahreszins / Sollzins";
    } else if (this.windowSize === 'desktop' && this.isSantander()) {
      return "Voraussichtl. eff. Jahreszins / Sollzins";
    } 
    return "Effektiver Jahreszins / Sollzins";
  }

  getInterestRateHintData(): { text: string }[] {
      return [{
        text: this.isSantander()
          ? 'Der voraussichtliche Sollzins zeigt, wie hoch die Zinsen für den Kredit sind. Der voraussichtliche effektive Jahreszins zeigt die jährlichen Gesamtkosten des Kredits und ist daher der wichtigste Wert für den Vergleich.'
          : 'Der Sollzins zeigt, wie hoch die Zinsen für den Kredit sind. Der effektive Jahreszins zeigt die jährlichen Gesamtkosten des Kredits und ist daher der wichtigste Wert für den Vergleich.',
      }];
  }
}
